import React, { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Sidebar from '../components/Sidebar';
import Toast from '../components/Toast';
import { useAdminComments } from '../hooks/useAdminComments';
import CommenterBadge from '../components/CommenterBadge';
import {
  COMMENTER_FILTER_OPTIONS,
  COMMENT_SORT_OPTIONS,
  buildCommenterParams,
  parseMoreThan,
} from '../utils/commenterFilter';
import { useCommentActions } from '../hooks/useCommentActions';
import { useAiReplySuggestions } from '../hooks/useAiReplySuggestions';
import AiProductRecommendations, { appendProductBlock } from '../components/AiProductRecommendations';
import { getApiClient } from '../api/apiClient';

const apiClient = getApiClient('kisanVani');

function CommentReplyBox({ commentId, replyText, onTextChange, onSubmit, onCancel, isSubmitting }) {
  const { data: suggestionsData, isLoading } = useAiReplySuggestions(commentId);
  const suggestions = suggestionsData?.suggestions ?? [];

  return (
    <div style={{
      marginTop: 12, padding: 14, background: '#f8fff8',
      borderRadius: 8, border: '1px solid #c8e6c9',
    }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{
          fontSize: 12, color: '#555', marginBottom: 6,
          display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500,
        }}>
          <span>✨</span>
          <span>AI Suggestions</span>
          {isLoading && <span style={{ color: '#aaa', fontWeight: 400 }}>fetching…</span>}
        </div>
        {suggestions.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onTextChange(s)}
                title={s}
                style={{
                  padding: '5px 12px', background: '#e8f5e9',
                  border: '1px solid #a5d6a7', borderRadius: 16,
                  fontSize: 12, color: '#2e7d32', cursor: 'pointer',
                  textAlign: 'left', lineHeight: 1.4,
                }}
              >
                {s.length > 90 ? s.slice(0, 90) + '…' : s}
              </button>
            ))}
          </div>
        ) : !isLoading ? (
          <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>
            No AI suggestions available
          </div>
        ) : null}
      </div>
      <AiProductRecommendations
        data={suggestionsData}
        isLoading={isLoading}
        onInsert={(block) => onTextChange(appendProductBlock(replyText, block))}
      />
      <textarea
        value={replyText}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder="Write your expert reply…"
        rows={3}
        autoFocus
        style={{
          width: '100%', padding: 10, borderRadius: 6,
          border: '1px solid #ccc', fontSize: 14, resize: 'vertical',
          boxSizing: 'border-box', fontFamily: 'Poppins, sans-serif',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '8px 14px', background: '#f5f5f5',
            border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13,
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          style={{
            padding: '8px 16px',
            background: isSubmitting ? '#90caf9' : '#1976d2',
            color: '#fff', border: 'none', borderRadius: 6,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            fontWeight: 600, fontSize: 13,
          }}
        >
          {isSubmitting ? 'Sending…' : 'Publish reply'}
        </button>
      </div>
    </div>
  );
}

const getStatusColor = (status) => {
  if (status === 'approved') return '#4CAF50';
  if (status === 'rejected') return '#f44336';
  if (status === 'pending') return '#FF9800';
  return '#999';
};

const NO_FILTERS = {
  status: '', hasReplies: null, fromDate: '', toDate: '',
  commenterType: 'all', moreThan: '', sortBy: 'newest',
};

const AdminComments = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const [hasRepliesFilter, setHasRepliesFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [commenterType, setCommenterType] = useState('all');
  const [moreThan, setMoreThan] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const [applied, setApplied] = useState(NO_FILTERS);

  const [toast, setToast] = useState(null);
  const [replyTarget, setReplyTarget] = useState(null); // { commentId, postId }
  const [replyText, setReplyText] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null); // { comment, actionType }
  const [remarks, setRemarks] = useState('');

  const queryClient = useQueryClient();
  const { approveComment } = useCommentActions();

  const staffReply = useMutation({
    mutationFn: async ({ postId, parentPostCommentId, comment }) => {
      const resp = await apiClient.post('/posts/comment', { postId, comment, parentPostCommentId });
      if (!resp.data?.success) throw new Error(resp.data?.message || 'Failed to post reply');
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-comments'] });
    },
  });

  const hasRepliesBool =
    applied.hasReplies === 'true' ? true :
    applied.hasReplies === 'false' ? false : null;

  const { data: comments, isLoading, error, refetch } = useAdminComments({
    status: applied.status || undefined,
    hasReplies: hasRepliesBool,
    fromDate: applied.fromDate || undefined,
    toDate: applied.toDate || undefined,
    ...buildCommenterParams({ commenterType: applied.commenterType, moreThan: applied.moreThan }),
    sortBy: applied.sortBy === 'commentCount' ? 'commentCount' : undefined,
    page,
    limit: 20,
  });

  // "More than … comments" needs a number before it can be applied.
  const customCountInvalid = commenterType === 'custom' && parseMoreThan(moreThan) === null;

  const handleApplyFilter = () => {
    setPage(1);
    setApplied({
      status: statusFilter,
      hasReplies: hasRepliesFilter,
      fromDate: startDate,
      toDate: endDate,
      commenterType,
      moreThan,
      sortBy,
    });
  };

  const handleClearFilter = () => {
    setStatusFilter('');
    setHasRepliesFilter('');
    setStartDate('');
    setEndDate('');
    setCommenterType('all');
    setMoreThan('');
    setSortBy('newest');
    setPage(1);
    setApplied(NO_FILTERS);
  };

  const handleApprove = (comment) => {
    if (comment.status !== 'pending') {
      setToast({ message: 'Comment has already been processed', type: 'warning' });
      return;
    }
    setConfirmDialog({ comment, actionType: 'approve' });
  };

  const handleReject = (comment) => {
    if (comment.status !== 'pending') {
      setToast({ message: 'Comment has already been processed', type: 'warning' });
      return;
    }
    setConfirmDialog({ comment, actionType: 'reject' });
  };

  const handleConfirm = async () => {
    if (!confirmDialog) return;
    const { comment, actionType } = confirmDialog;
    if (actionType === 'reject' && !remarks.trim()) {
      setToast({ message: 'Please provide remarks for rejection', type: 'warning' });
      return;
    }
    try {
      await approveComment.mutateAsync({
        postCommentId: comment.postCommentId,
        status: actionType === 'approve' ? 'approved' : 'rejected',
        remarks: actionType === 'reject' ? remarks : undefined,
      });
      setToast({
        message: `Comment ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`,
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-comments'] });
    } catch (err) {
      setToast({ message: err.message || 'Failed to process comment', type: 'error' });
    } finally {
      setConfirmDialog(null);
      setRemarks('');
    }
  };

  const handleReplyClick = (comment) => {
    if (replyTarget?.commentId === comment.postCommentId) {
      setReplyTarget(null);
      setReplyText('');
    } else {
      setReplyTarget({ commentId: comment.postCommentId, postId: comment.postId });
      setReplyText('');
    }
  };

  const isSubmittingReplyRef = useRef(false);

  const handleSubmitReply = async () => {
    // Synchronous guard against double-clicks; `isPending` updates only after a re-render.
    if (isSubmittingReplyRef.current) return;
    if (!replyText.trim()) {
      setToast({ message: 'Please enter a reply', type: 'warning' });
      return;
    }
    isSubmittingReplyRef.current = true;
    try {
      await staffReply.mutateAsync({
        postId: replyTarget.postId,
        parentPostCommentId: replyTarget.commentId,
        comment: replyText.trim(),
      });
      setToast({ message: 'Reply published successfully', type: 'success' });
      setReplyTarget(null);
      setReplyText('');
    } catch (err) {
      setToast({ message: err.message || 'Failed to send reply', type: 'error' });
    } finally {
      isSubmittingReplyRef.current = false;
    }
  };

  const staffBadgeLabel = (role) => {
    if (!role) return 'Staff';
    const r = String(role).toLowerCase();
    if (r === 'admin') return 'Admin';
    if (r === 'expert') return 'Expert';
    if (r === 'support') return 'Support';
    return 'Staff';
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#fafaf7', fontFamily: 'Poppins, sans-serif' }}>
      <Sidebar active="allcomments" />
      <main style={{ flex: 1, marginLeft: 280, padding: '2rem 2rem 4rem 32px', minHeight: '100vh' }}>
        <h1 style={{ fontWeight: 700, fontSize: 26, color: '#18492b', marginBottom: 4 }}>All Comments</h1>
        <div style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>
          Moderation view across all KisanVani posts
        </div>

        {/* Filter Bar */}
        <div style={{
          background: '#fff', borderRadius: 10, padding: 20,
          boxShadow: '0 2px 8px #0001', marginBottom: 24,
          display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end',
        }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 500 }}>
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd',
                fontSize: 14, background: '#fff', minWidth: 130, cursor: 'pointer',
              }}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 500 }}>
              Replies
            </label>
            <select
              value={hasRepliesFilter}
              onChange={(e) => setHasRepliesFilter(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd',
                fontSize: 14, background: '#fff', minWidth: 150, cursor: 'pointer',
              }}
            >
              <option value="">All</option>
              <option value="false">Awaiting reply</option>
              <option value="true">Has replies</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="commenter-filter"
              title="Counts are each commenter's lifetime comments. Staff accounts and staff replies are not counted."
              style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 500 }}
            >
              Commenters
            </label>
            <select
              id="commenter-filter"
              value={commenterType}
              onChange={(e) => setCommenterType(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd',
                fontSize: 14, background: '#fff', minWidth: 190, cursor: 'pointer',
              }}
            >
              {COMMENTER_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {commenterType === 'custom' && (
            <div>
              <label
                htmlFor="commenter-more-than"
                style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 500 }}
              >
                More than (comments)
              </label>
              <input
                id="commenter-more-than"
                type="number"
                min="0"
                step="1"
                value={moreThan}
                onChange={(e) => setMoreThan(e.target.value)}
                placeholder="e.g. 5"
                style={{
                  padding: '8px 10px', borderRadius: 6, width: 110, fontSize: 14,
                  border: `1px solid ${customCountInvalid ? '#ef9a9a' : '#ddd'}`,
                }}
              />
            </div>
          )}

          <div>
            <label
              htmlFor="comment-sort"
              style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 500 }}
            >
              Sort by
            </label>
            <select
              id="comment-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd',
                fontSize: 14, background: '#fff', minWidth: 170, cursor: 'pointer',
              }}
            >
              {COMMENT_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 500 }}>
              From date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14 }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 500 }}>
              To date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14 }}
            />
          </div>

          <button
            onClick={handleApplyFilter}
            disabled={customCountInvalid}
            title={customCountInvalid ? 'Enter a number of comments' : undefined}
            style={{
              padding: '9px 22px', background: '#2e7d32', color: '#fff',
              border: 'none', borderRadius: 6,
              cursor: customCountInvalid ? 'not-allowed' : 'pointer',
              opacity: customCountInvalid ? 0.5 : 1,
              fontSize: 14, fontWeight: 500,
            }}
          >
            Apply Filter
          </button>
          <button
            onClick={handleClearFilter}
            style={{
              padding: '9px 20px', background: '#f5f5f5', color: '#333',
              border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 14,
            }}
          >
            Clear
          </button>
        </div>

        {/* Comment List */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#666' }}>
            <div style={{
              width: 40, height: 40, margin: '0 auto 16px',
              border: '4px solid #f3f3f3', borderTop: '4px solid #2e7d32',
              borderRadius: '50%', animation: 'spin 1s linear infinite',
            }} />
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            Loading comments…
          </div>
        ) : error ? (
          <div style={{
            padding: 24, background: '#ffebee', borderRadius: 8,
            color: '#c62828', textAlign: 'center',
          }}>
            Error: {error.message}
            <button
              onClick={refetch}
              style={{ marginLeft: 16, padding: '6px 14px', cursor: 'pointer', borderRadius: 4, border: '1px solid #c62828' }}
            >
              Retry
            </button>
          </div>
        ) : !comments?.length ? (
          <div style={{
            padding: 48, background: '#fff', borderRadius: 10,
            textAlign: 'center', color: '#666', boxShadow: '0 2px 8px #0001',
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
            <div style={{ fontWeight: 500, fontSize: 16 }}>No comments found</div>
            <div style={{ fontSize: 14, marginTop: 4 }}>Try adjusting your filters</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {comments.map((comment) => (
              <div
                key={comment.postCommentId}
                style={{
                  background: '#fff', borderRadius: 10, padding: 20,
                  boxShadow: '0 2px 8px #0001',
                  borderLeft: `4px solid ${getStatusColor(comment.status)}`,
                }}
              >
                {/* Post title row */}
                <div style={{
                  fontSize: 12, color: '#888', marginBottom: 10,
                  display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                }}>
                  <span>📄</span>
                  <span style={{ fontWeight: 600, color: '#18492b', fontSize: 13 }}>
                    {comment.postTitle || 'Unknown post'}
                  </span>
                  {comment.parentPostCommentId && (
                    <span style={{ color: '#aaa' }}>· reply to comment</span>
                  )}
                  {comment.isStaffReply && (
                    <span style={{
                      background: '#dcedc8', color: '#1b5e20',
                      padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                    }}>
                      {staffBadgeLabel(comment.staffReplyRole)}
                    </span>
                  )}
                </div>

                {/* User info + meta */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 12, flexWrap: 'wrap', gap: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: '#e8f5e9', color: '#2e7d32',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 14, flexShrink: 0,
                    }}>
                      {(comment.firstName || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>
                        {`${comment.firstName || ''} ${comment.lastName || ''}`.trim() || 'Unknown'}
                      </div>
                      <div style={{ fontSize: 12, color: '#888' }}>{comment.phone || ''}</div>
                      <div style={{ marginTop: 4 }}>
                        <CommenterBadge comment={comment} />
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 12, color: '#aaa' }}>
                      {comment.createdAt ? new Date(comment.createdAt).toLocaleString() : ''}
                    </div>
                    <div style={{
                      padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                      background:
                        comment.status === 'approved' ? '#e8f5e8' :
                        comment.status === 'rejected' ? '#ffebee' : '#fff3e0',
                      color: getStatusColor(comment.status),
                      border: `1px solid ${getStatusColor(comment.status)}55`,
                    }}>
                      {comment.status === 'approved' ? '✓ Approved' :
                       comment.status === 'rejected' ? '✗ Rejected' : '⏳ Pending'}
                    </div>
                    <div style={{
                      padding: '3px 10px', borderRadius: 99, fontSize: 11,
                      background: comment.hasReplies ? '#e3f2fd' : '#f5f5f5',
                      color: comment.hasReplies ? '#1565c0' : '#999',
                    }}>
                      {comment.hasReplies ? '💬 Has replies' : '○ No replies'}
                    </div>
                  </div>
                </div>

                {/* Comment text */}
                <div style={{
                  fontSize: 14, color: '#333', lineHeight: 1.65,
                  padding: '10px 14px', background: '#fafafa', borderRadius: 6,
                  marginBottom: 14,
                }}>
                  {comment.comment}
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {!comment.isStaffReply && (
                    <button
                      type="button"
                      onClick={() => handleReplyClick(comment)}
                      style={{
                        padding: '6px 14px', background: '#e3f2fd',
                        color: '#0d47a1', border: '1px solid #90caf9',
                        borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500,
                      }}
                    >
                      {replyTarget?.commentId === comment.postCommentId ? '✕ Close' : '💬 Reply'}
                    </button>
                  )}

                  {comment.status === 'pending' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleApprove(comment)}
                        disabled={approveComment.isPending}
                        style={{
                          padding: '6px 14px', background: '#4CAF50', color: '#fff',
                          border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500,
                          cursor: approveComment.isPending ? 'not-allowed' : 'pointer',
                        }}
                      >
                        ✓ Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReject(comment)}
                        disabled={approveComment.isPending}
                        style={{
                          padding: '6px 14px', background: '#f44336', color: '#fff',
                          border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500,
                          cursor: approveComment.isPending ? 'not-allowed' : 'pointer',
                        }}
                      >
                        ✗ Reject
                      </button>
                    </>
                  ) : (
                    <div style={{
                      padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                      background: comment.status === 'approved' ? '#e8f5e8' : '#ffebee',
                      color: comment.status === 'approved' ? '#2e7d32' : '#c62828',
                      border: `1px solid ${getStatusColor(comment.status)}30`,
                    }}>
                      {comment.status === 'approved' ? '✓ Already Approved' : '✗ Already Rejected'}
                    </div>
                  )}
                </div>

                {/* Reply box with AI suggestions */}
                {replyTarget?.commentId === comment.postCommentId && (
                  <CommentReplyBox
                    commentId={comment.postCommentId}
                    postId={comment.postId}
                    replyText={replyText}
                    onTextChange={setReplyText}
                    onSubmit={handleSubmitReply}
                    onCancel={() => { setReplyTarget(null); setReplyText(''); }}
                    isSubmitting={staffReply.isPending}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {comments?.length > 0 && (
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            gap: 16, marginTop: 28,
          }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{
                padding: '8px 20px', border: '1px solid #ddd', borderRadius: 6,
                cursor: page <= 1 ? 'not-allowed' : 'pointer',
                background: page <= 1 ? '#f5f5f5' : '#fff',
                color: page <= 1 ? '#aaa' : '#333', fontSize: 14,
              }}
            >
              ← Prev
            </button>
            <span style={{ fontSize: 14, color: '#666' }}>Page {page}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!comments?.length || comments.length < 20}
              style={{
                padding: '8px 20px', border: '1px solid #ddd', borderRadius: 6,
                cursor: (!comments?.length || comments.length < 20) ? 'not-allowed' : 'pointer',
                background: (!comments?.length || comments.length < 20) ? '#f5f5f5' : '#fff',
                color: (!comments?.length || comments.length < 20) ? '#aaa' : '#333',
                fontSize: 14,
              }}
            >
              Next →
            </button>
          </div>
        )}
      </main>

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
          }}
          onClick={approveComment.isPending ? undefined : () => { setConfirmDialog(null); setRemarks(''); }}
        >
          <div
            style={{
              background: '#fff', padding: 28, borderRadius: 12,
              maxWidth: 420, width: '90%', textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#333' }}>
              Confirm {confirmDialog.actionType === 'approve' ? 'Approval' : 'Rejection'}
            </h3>
            <p style={{ color: '#666', marginBottom: 20, lineHeight: 1.5, fontSize: 14 }}>
              Are you sure you want to {confirmDialog.actionType} this comment?
            </p>
            {confirmDialog.actionType === 'reject' && (
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Provide reason for rejection"
                rows={3}
                style={{
                  width: '100%', padding: 10, borderRadius: 6,
                  border: '1px solid #ddd', marginBottom: 16,
                  resize: 'vertical', fontSize: 14, boxSizing: 'border-box',
                  fontFamily: 'Poppins, sans-serif',
                }}
              />
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={() => { setConfirmDialog(null); setRemarks(''); }}
                disabled={approveComment.isPending}
                style={{
                  padding: '8px 24px', background: '#f5f5f5', border: 'none',
                  borderRadius: 6, cursor: 'pointer', fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={approveComment.isPending}
                style={{
                  padding: '8px 24px',
                  background: confirmDialog.actionType === 'approve' ? '#4CAF50' : '#f44336',
                  color: '#fff', border: 'none', borderRadius: 6,
                  cursor: approveComment.isPending ? 'not-allowed' : 'pointer',
                  fontSize: 14, fontWeight: 600,
                }}
              >
                {approveComment.isPending ? 'Processing…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default AdminComments;
